import { useEffect, useState } from 'react';
import { DesignPicker } from '../components/DesignPicker';
import { ConfirmDialog } from '../components/Modal';
import { ACCENT_CONTRAST, ACCENT_PRESETS, applyAccent, currentAccent, DEFAULT_ACCENT, isHexColor } from '../lib/accent';
import { contrastRatio } from '../lib/contrast';
import { pushToast } from '../lib/events';
import { useAccounts, type AccountDto } from '../lib/roster';
import { useRotateShareLink, useShareLinks, useUpsertShareLink, type ShareLinkDto } from '../lib/shareLinks';
import { useDesign } from '../lib/design';
import { applyTheme, currentTheme, THEME_PRESETS, type ThemeSlug } from '../lib/theme';

const AA = 4.5;

/**
 * `/settings` — account-wide preferences: the theme picker and the accent picker (docs/DESIGN.md,
 * REV10). Other settings groups can be added as their own sections later without disturbing these.
 *
 * <p>The boss-visibility section that used to live here was removed 2026-08-16: the Bosses tab
 * redesign (character tab strip + opt-in boss lists, see `docs/specs/POST-MVP-fixes.md` §8)
 * supersedes hiding a boss from an account-wide matrix — a character simply never has it on its
 * list. The `hidden_boss` table and its `PUT`/`DELETE /api/characters/{id}/hidden-bosses/{slug}`
 * endpoints are left untouched on the backend (harmless, no longer reachable from any UI); see that
 * spec entry for the deprecation note.
 */
export function Settings() {
  // Lifted so the theme cards' accent dot and the accent section's live selection stay in sync
  // without either section reaching into the other's state.
  const [accent, setAccent] = useState(() => currentAccent());
  // REV30: a non-Classic design owns its own surfaces AND its own accent (both picked inside
  // DesignPicker), so Classic's theme cards and accent section would be dead/misleading controls.
  const design = useDesign();

  return (
    <div className="stagger-children space-y-6">
      <div>
        <h1 className="font-display text-xl font-semibold text-text">Settings</h1>
        <p className="mt-1 text-sm text-text-muted">Account, world and display preferences.</p>
      </div>
      <DesignPicker />
      {design == null && <ThemeSection accent={accent} />}
      {design == null && <AccentSection accent={accent} setAccent={setAccent} />}
      <PublicProfileSection />
    </div>
  );
}

/**
 * The theme picker (REV10): five cards, each a mini preview (stacked bg/elevated/text bars + a dot
 * in the current accent) rather than a bare colour circle, applied live via `applyTheme` (which
 * sets/removes `data-theme` on `<html>` and persists to localStorage). Picking "Maple Night"
 * removes the attribute -- that's the zero-`data-theme` default, not a fifth override.
 */
function ThemeSection({ accent }: { accent: string }) {
  const [selected, setSelected] = useState<ThemeSlug | null>(() => currentTheme());

  const choose = (slug: ThemeSlug | null) => {
    applyTheme(slug);
    setSelected(slug);
  };

  return (
    <section className="rounded-xl border border-border bg-bg-elevated p-4 sm:p-5">
      <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-text-faint">Theme</h2>
      <p className="mt-1 max-w-prose text-sm text-text-muted">
        Swaps the app's surface colours. The accent above still applies on top of any theme. Saved
        on this device.
      </p>
      <div className="mt-4 flex flex-wrap gap-3" role="radiogroup" aria-label="Theme">
        {THEME_PRESETS.map((preset) => {
          const isSelected = preset.slug === selected;
          return (
            <button
              key={preset.name}
              type="button"
              role="radio"
              aria-checked={isSelected}
              onClick={() => choose(preset.slug)}
              className={[
                'flex min-h-tap w-[6.5rem] flex-col items-center gap-1.5 rounded-lg border-2 p-2 transition-transform hover:scale-105',
                isSelected ? 'border-text' : 'border-transparent',
              ].join(' ')}
            >
              <span
                aria-hidden="true"
                className="relative flex h-12 w-full flex-col justify-end gap-0.5 overflow-hidden rounded-md border border-border-strong/40 p-1.5"
                style={{ backgroundColor: preset.swatch.bg }}
              >
                <span
                  className="block h-2.5 w-3/4 rounded-sm"
                  style={{ backgroundColor: preset.swatch.elevated }}
                />
                <span className="block h-1 w-1/2 rounded-sm" style={{ backgroundColor: preset.swatch.text }} />
                <span
                  aria-hidden="true"
                  className="absolute right-1.5 top-1.5 size-2 rounded-full"
                  style={{ backgroundColor: accent }}
                />
                {isSelected && (
                  <span
                    aria-hidden="true"
                    className="absolute inset-0 flex items-center justify-center bg-black/25 text-sm font-bold text-white"
                  >
                    ✓
                  </span>
                )}
              </span>
              <span className="text-xs font-semibold text-text">{preset.name}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

/**
 * The accent picker (docs/DESIGN.md): twelve curated swatches plus a free custom colour, applied
 * live via `applyAccent` (which sets `--accent` inline on `<html>` and persists to localStorage) so
 * the shell glow, nav, stamps and buttons re-tint immediately, with no reload.
 */
function AccentSection({ accent, setAccent }: { accent: string; setAccent: (color: string) => void }) {
  const isPreset = ACCENT_PRESETS.some((preset) => preset.color === accent);
  const customContrast = contrastRatio(accent, ACCENT_CONTRAST);
  const showCustomWarning = !isPreset && customContrast < AA;

  const choose = (color: string) => {
    applyAccent(color === DEFAULT_ACCENT ? null : color);
    setAccent(color);
  };

  const chooseCustom = (color: string) => {
    if (!isHexColor(color)) return;
    choose(color.toLowerCase());
  };

  return (
    <section className="rounded-xl border border-border bg-bg-elevated p-4 sm:p-5">
      <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-text-faint">Accent colour</h2>
      <p className="mt-1 max-w-prose text-sm text-text-muted">
        Retints the shell glow, navigation, stamps and buttons throughout the app. Saved on this
        device.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3" role="radiogroup" aria-label="Accent colour">
        {ACCENT_PRESETS.map((preset) => {
          const isSelected = preset.color === accent;
          return (
            <button
              key={preset.color}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={preset.name}
              title={preset.name}
              onClick={() => choose(preset.color)}
              className={[
                'flex min-h-tap min-w-tap items-center justify-center rounded-full border-2 p-1 transition-transform hover:scale-105',
                isSelected ? 'border-text' : 'border-transparent',
              ].join(' ')}
            >
              <span
                aria-hidden="true"
                className="size-8 rounded-full shadow-inner"
                style={{ backgroundColor: preset.color }}
              />
            </button>
          );
        })}
        <label
          className={[
            'flex min-h-tap min-w-tap cursor-pointer items-center justify-center rounded-full border-2 p-1 transition-transform hover:scale-105',
            !isPreset ? 'border-text' : 'border-transparent',
          ].join(' ')}
          title="Custom…"
        >
          <span
            aria-hidden="true"
            className="relative flex size-8 items-center justify-center rounded-full shadow-inner"
            style={{
              backgroundColor: !isPreset ? accent : undefined,
              backgroundImage: isPreset
                ? 'conic-gradient(from 0deg, #ff5d47, #e8c13a, #9ed44a, #3ecfd8, #5fb4ff, #b18cff, #ff5fb8, #ff5d47)'
                : undefined,
            }}
          >
            {isPreset && <span className="text-[0.6rem] font-bold text-white drop-shadow">+</span>}
          </span>
          <input
            type="color"
            aria-label="Custom accent colour"
            value={isHexColor(accent) ? accent : DEFAULT_ACCENT}
            onChange={(event) => chooseCustom(event.target.value)}
            className="sr-only"
          />
        </label>
        <span className="text-xs font-medium text-text-muted">Custom…</span>
      </div>
      {showCustomWarning && (
        <p className="mt-3 max-w-prose text-sm text-warning" role="alert">
          This accent may be hard to read on buttons ({customContrast.toFixed(1)}:1 contrast, below
          the 4.5:1 minimum).
        </p>
      )}
    </section>
  );
}

// -------------------------------------------------------------------------------------------
// Public profile (REV23 — docs/specs/REV23-share-profile.md)
// -------------------------------------------------------------------------------------------

const checkboxClasses =
  'size-4 shrink-0 rounded border-border-strong bg-bg-elevated-hover text-accent focus:ring-accent';

/**
 * One block per account: enable toggle, display name, the three section toggles, a copy-link
 * button, and a rotate-link button behind an "old link stops working" confirm. `PUT
 * /api/share-links/{accountId}` is a single create-or-update write, so every field here is staged
 * locally and only sent on "Save" — there's no per-field autosave to keep in sync with a partial
 * "leave unchanged" convention (unlike the roster PATCH endpoints, this one always sends its whole
 * current state).
 */
export function PublicProfileSection() {
  const accountsQuery = useAccounts();
  const linksQuery = useShareLinks();
  const accounts = accountsQuery.data ?? [];

  return (
    <section className="rounded-xl border border-border bg-bg-elevated p-4 sm:p-5">
      <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-text-faint">
        Public profile
      </h2>
      <p className="mt-1 max-w-prose text-sm text-text-muted">
        Share a read-only link to one account&rsquo;s roster, and optionally its Legion, HEXA and
        Pitched progress. Off by default, unguessable, and revocable at any time. Daily/weekly
        tasks, boss clears, crystal income and notes are never shared — a share link only ever
        carries long-horizon progress.
      </p>
      <div className="mt-4 space-y-4">
        {accountsQuery.isPending || linksQuery.isPending ? (
          <p className="text-sm text-text-muted">Loading your accounts…</p>
        ) : accounts.length === 0 ? (
          <p className="text-sm text-text-muted">
            Add an account under Characters first — a share link is created per account.
          </p>
        ) : (
          accounts.map((account) => (
            <AccountShareCard
              key={account.id}
              account={account}
              existing={(linksQuery.data ?? []).find((link) => link.accountId === account.id) ?? null}
            />
          ))
        )}
      </div>
    </section>
  );
}

function AccountShareCard({ account, existing }: { account: AccountDto; existing: ShareLinkDto | null }) {
  const upsert = useUpsertShareLink();
  const rotate = useRotateShareLink();

  const [displayName, setDisplayName] = useState(existing?.displayName ?? account.label);
  const [enabled, setEnabled] = useState(existing?.enabled ?? false);
  const [showLegion, setShowLegion] = useState(existing?.showLegion ?? true);
  const [showHexa, setShowHexa] = useState(existing?.showHexa ?? true);
  const [showPitched, setShowPitched] = useState(existing?.showPitched ?? true);
  const [showLinks, setShowLinks] = useState(existing?.showLinks ?? true);
  const [confirmingRotate, setConfirmingRotate] = useState(false);

  // Hydrate local state from the server's own row exactly once (the first time it lands) — the
  // form is otherwise "always send full current state on Save", so a later background refetch
  // (e.g. after Save itself) must never clobber whatever the user is mid-typing.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (hydrated) return;
    if (existing) {
      setDisplayName(existing.displayName);
      setEnabled(existing.enabled);
      setShowLegion(existing.showLegion);
      setShowHexa(existing.showHexa);
      setShowPitched(existing.showPitched);
      setShowLinks(existing.showLinks);
    }
    setHydrated(true);
    // Only the initial hydration matters — this effect intentionally never re-runs on `existing`
    // updates once hydrated.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing]);

  const shareUrl = existing ? `${window.location.origin}/s/${existing.token}` : null;

  const save = () => {
    const trimmed = displayName.trim();
    if (!trimmed) {
      pushToast('Give this profile a display name first.');
      return;
    }
    upsert.mutate({
      accountId: account.id,
      input: { displayName: trimmed, enabled, showLegion, showHexa, showPitched, showLinks },
    });
  };

  const copyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      pushToast('Link copied.', 'success');
    } catch {
      pushToast('Could not copy automatically — copy the link text directly instead.');
    }
  };

  return (
    <div className="rounded-lg border border-border bg-bg-elevated-hover p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-text">{account.label}</p>
          <p className="text-xs text-text-faint">
            {existing?.enabled ? 'Shared' : 'Not shared'}
          </p>
        </div>
        <label className="flex min-h-tap items-center gap-2 text-sm font-medium text-text">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className={checkboxClasses}
          />
          Enable public profile
        </label>
      </div>

      {!enabled && (
        <p className="mt-3 text-xs text-text-faint">
          Off by default. When enabled, an unguessable link shows this account&rsquo;s roster
          (characters, classes, levels, avatars, force values) plus whichever of
          Legion/HEXA/Pitched/Link Skills you turn on below — never checklists, boss clears,
          income, or your Discord identity.
        </p>
      )}

      <div className="mt-3 space-y-3">
        <div>
          <label htmlFor={`share-name-${account.id}`} className="mb-1 block text-xs font-medium text-text-muted">
            Display name (shown on the link, not your Discord name)
          </label>
          <input
            id={`share-name-${account.id}`}
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={60}
            className="min-h-tap w-full rounded-lg border border-border-strong bg-bg-elevated px-3 text-sm text-text focus:border-accent focus:outline-none"
          />
        </div>

        <div className="flex flex-wrap gap-4">
          <label className="flex min-h-tap items-center gap-2 text-sm text-text-muted">
            <input
              type="checkbox"
              checked={showLegion}
              onChange={(e) => setShowLegion(e.target.checked)}
              className={checkboxClasses}
            />
            Legion
          </label>
          <label className="flex min-h-tap items-center gap-2 text-sm text-text-muted">
            <input
              type="checkbox"
              checked={showHexa}
              onChange={(e) => setShowHexa(e.target.checked)}
              className={checkboxClasses}
            />
            HEXA
          </label>
          <label className="flex min-h-tap items-center gap-2 text-sm text-text-muted">
            <input
              type="checkbox"
              checked={showPitched}
              onChange={(e) => setShowPitched(e.target.checked)}
              className={checkboxClasses}
            />
            Pitched
          </label>
          <label className="flex min-h-tap items-center gap-2 text-sm text-text-muted">
            <input
              type="checkbox"
              checked={showLinks}
              onChange={(e) => setShowLinks(e.target.checked)}
              className={checkboxClasses}
            />
            Link Skills
          </label>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={save}
            disabled={upsert.isPending}
            className="min-h-tap rounded-lg bg-accent px-4 text-sm font-semibold text-accent-contrast shadow-sm transition-colors hover:bg-accent-strong disabled:opacity-50"
          >
            {upsert.isPending ? 'Saving…' : 'Save'}
          </button>

          {existing && (
            <>
              <button
                type="button"
                onClick={copyLink}
                className="min-h-tap rounded-lg border border-border-strong px-4 text-sm font-semibold text-text transition-colors hover:bg-bg-elevated"
              >
                Copy link
              </button>
              <button
                type="button"
                onClick={() => setConfirmingRotate(true)}
                className="min-h-tap rounded-lg border border-border-strong px-4 text-sm font-semibold text-text-muted transition-colors hover:bg-bg-elevated"
              >
                Rotate link
              </button>
            </>
          )}
        </div>

        {shareUrl && (
          <p className="break-all text-xs text-text-faint">{shareUrl}</p>
        )}
      </div>

      {confirmingRotate && (
        <ConfirmDialog
          title="Rotate share link"
          message="This generates a brand-new link and immediately breaks the old one — anyone who had it will get a 'not found' page. This cannot be undone."
          confirmLabel="Rotate"
          danger
          pending={rotate.isPending}
          onCancel={() => setConfirmingRotate(false)}
          onConfirm={() => rotate.mutate(account.id, { onSuccess: () => setConfirmingRotate(false) })}
        />
      )}
    </div>
  );
}
